import type { CollectionModalScreenNavigationProp } from "../../../../navigation/types";
import type { CollectionModalProps } from "../components/common/CollectionModal";

import { useIsDesktopDisplay } from "../../../../context/ViewJobDisplayContext";
import {
  AttributeTooltipModal,
  type AttributeTooltipModalProps,
} from "../components/common/AttributeTooltipModal";
import {
  CollectionModalOverlay,
  type CollectionModalOverlayProps,
} from "../components/common/CollectionModal/CollectionModalOverlay";

import { useNavigation } from "@react-navigation/native";
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";

export type ContextModalProps = Omit<CollectionModalProps, "onExit" | "setData">;

// Separate callbacks from serializable data
type CollectionModalCallbacks = {
  onModalSubmit: CollectionModalProps["onModalSubmit"];
  onModalReset: CollectionModalProps["onModalReset"];
  onClose?: CollectionModalProps["onClose"];
  onCleanup?: () => void;
};

type ContextT = {
  openCollectionModal: (props: ContextModalProps, loggedIn: boolean) => void;
  hasSubmitted: boolean;
  setHasSubmitted: (v: boolean) => void;
  // Store callbacks separately - expose the ref for immediate access
  collectionModalCallbacksRef: React.MutableRefObject<CollectionModalCallbacks>;
  setAttributeTooltipModalProps: Dispatch<SetStateAction<AttributeTooltipModalProps>>;
  dismissAttributeTooltipModal: () => void;
  setModalCleanupCallback: (callback: (() => void) | null) => void;
};

const ModalContext = createContext<ContextT>({
  openCollectionModal: () => undefined,
  hasSubmitted: false,

  setHasSubmitted: () => undefined,
  collectionModalCallbacksRef: {
    current: {
      onModalSubmit: () => Promise.reject(new Error("No onModalSubmit callback")),
      onModalReset: () => Promise.reject(new Error("No onModalReset callback")),
    },
  },
  setAttributeTooltipModalProps: (q) => q,
  dismissAttributeTooltipModal: () => undefined,
  setModalCleanupCallback: () => undefined,
});

export function useModalContext() {
  return useContext(ModalContext);
}

export const JMUIModalContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const collectionNavigation = useNavigation<CollectionModalScreenNavigationProp>();
  const isDesktop = useIsDesktopDisplay();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Desktop (web) presents the Collection modal as a centered overlay instead
  // of the mobile full-screen navigation screen (RN-5815). Null when closed.
  const [collectionModalOverlayProps, setCollectionModalOverlayProps] = useState<Omit<
    CollectionModalOverlayProps,
    "isOpen" | "onClose"
  > | null>(null);

  const [attributeTooltipModalProps, setAttributeTooltipModalProps] =
    useState<AttributeTooltipModalProps>({
      title: "",
      content: null,
      isOpen: false,
      onDismiss: () => undefined,
      labelClose: "",
    });

  // Store callbacks in ref for immediate access
  const collectionModalCallbacksRef = useRef<CollectionModalCallbacks>({
    onModalSubmit: () => Promise.reject(new Error("No onModalSubmit callback")),
    onModalReset: () => Promise.reject(new Error("No onModalReset callback")),
  });

  const dismissAttributeTooltipModal = () => {
    setAttributeTooltipModalProps((prev) => ({ ...prev, isOpen: false }));
  };

  const setModalCleanupCallback = (callback: (() => void) | null) => {
    collectionModalCallbacksRef.current.onCleanup = callback || undefined;
  };

  const closeCollectionModalOverlay = () => {
    setCollectionModalOverlayProps(null);
    // Restore focus to the tile that opened the modal (parity with the
    // navigation screen's unmount cleanup).
    collectionModalCallbacksRef.current.onCleanup?.();
  };

  const openCollectionModal = (modalProps: ContextModalProps, loggedIn: boolean) => {
    if (loggedIn) {
      // Extract callbacks from props
      const { onModalSubmit, onModalReset, onClose, ...serializableProps } = modalProps;

      // Store callbacks in ref (synchronous update)
      const callbacks = {
        onModalSubmit,
        onModalReset,
        onClose,
        onCleanup: collectionModalCallbacksRef.current.onCleanup,
      };
      collectionModalCallbacksRef.current = callbacks;

      if (isDesktop) {
        // Desktop web: render the centered overlay inline rather than
        // navigating to the full-screen modal screen (RN-5815).
        setCollectionModalOverlayProps(serializableProps);
      } else {
        // Navigate with only serializable props
        collectionNavigation.navigate("CollectionModal", serializableProps);
      }
    }
  };

  return (
    <ModalContext.Provider
      value={{
        openCollectionModal,
        hasSubmitted,
        setHasSubmitted,
        collectionModalCallbacksRef,
        setAttributeTooltipModalProps,
        dismissAttributeTooltipModal,
        setModalCleanupCallback,
      }}
    >
      {children}
      <AttributeTooltipModal {...attributeTooltipModalProps} />
      {collectionModalOverlayProps && (
        <CollectionModalOverlay
          {...collectionModalOverlayProps}
          isOpen
          onClose={closeCollectionModalOverlay}
        />
      )}
    </ModalContext.Provider>
  );
};
